import {Injectable} from '@angular/core';
import {UserStore} from '@sharedcommon/store/user.store';
import {Router} from '@angular/router';
import {UserService} from '@sharedcommon/services/user.service';
import {ResultSettingsService} from '@usercommon/service/result-settings.service';
import {MsalService} from '@azure/msal-angular';
import {AccountInfo, InteractionRequiredAuthError} from "@azure/msal-browser";
import { b2cPolicies, msalConfig, SHARED_AZURE_PROTECTED_SCOPE } from '../../config/auth-config';
import { UserLicense } from '@sharedcommon/model/user.model';

export interface ClaimsValidationResult {
  valid: boolean;
  error?: 'unvalid-account' | 'no-license' | 'license-expired' | 'user-not-found' | 'unknown';
}

@Injectable()
export class AuthenticationService {

  redirectUrl: string = '';

  constructor(
    private readonly userService: UserService,
    private readonly resultSettingsService: ResultSettingsService,
    private readonly store: UserStore,
    private readonly router: Router,
    private readonly msalService: MsalService) {
  }

  logout() {
    this.router.navigate(['logout'])
  }

  doLogout() {
    const azureAccount = this.setAndGetAzureAccount();
    if (azureAccount != null) {
      this.clearUserData();
      this.msalService.logoutRedirect({ account: azureAccount });
    }
  }

  clearUserData() {
    this.store.clear();
    this.userService.clear();
    this.resultSettingsService.clear();
  }

  async getSharedAuthorizationToken(): Promise<string> {
    const request = {
      scopes: [SHARED_AZURE_PROTECTED_SCOPE],
    };
    return await this.msalService.instance.acquireTokenSilent(request)
      .then(tokenResponse => {
        return 'Bearer ' + tokenResponse.accessToken;
      }).catch(async (error) => {
        if (error instanceof InteractionRequiredAuthError) {
          // fallback to interaction when silent call fails
          return this.msalService.instance.acquireTokenPopup(request)
            .then(tokenResponse => {
              return 'Bearer ' + tokenResponse.accessToken;
            });
        }
        return '';
      })
  }

  validateClaims(claims: any): ClaimsValidationResult {
    if (!claims) {
      return { valid: false, error: 'unvalid-account' };
    }

    if (claims.isForgotPassword) {
      this.router.navigate(['logout']);
      return { valid: false, error: 'unvalid-account' };
    }

    if (!claims['extension_user_uuid'] || claims['extension_user_uuid'] === 'USER_NOT_FOUND') {
      return { valid: false, error: 'user-not-found' };
    }

    // No license check for SADMIN user
    if (claims['extension_user_authorities']?.includes('SADMIN')) {
      return { valid: true };
    }

    if (!claims['extension_user_licenses']) {
      return { valid: false, error: 'no-license' };
    }

    let licenses: UserLicense[] = JSON.parse(claims['extension_user_licenses'] as string);
    let appLicenses = licenses.filter(license => license.product === 'P002');
    if (appLicenses.length === 0) {
      return { valid: false, error: 'no-license' };
    }

    if (appLicenses.filter(license => license.valid).length === 0) {
      return { valid: false, error: 'license-expired' };
    }

    return { valid: true };
    }

  setAndGetAzureAccount(): AccountInfo | null {
    let accounts = this.msalService.instance.getAllAccounts().filter(a => this.accountFilter(a));
    if (accounts.length > 0) {
      this.msalService.instance.setActiveAccount(accounts[0]);
    }
    else
    {
      this.msalService.instance.setActiveAccount(null);
    }

    return this.msalService.instance.getActiveAccount();
  }

  accountFilter(account: AccountInfo): boolean {
    const tfp = account?.idTokenClaims?.tfp as string ?? '';
    const aud = account?.idTokenClaims?.aud ?? '';
    const envAccount = account?.environment ?? '';
    return envAccount.toLowerCase() == b2cPolicies.authorityDomain.toLowerCase() && aud.toLowerCase() == msalConfig.auth.clientId.toLowerCase() && tfp.toLowerCase() == b2cPolicies.names.signUpSignIn.toLowerCase();
  }
}
